/**
 * FixtureDetailDialog.tsx — the fixture DOSSIER (2026-07-30 redesign).
 *
 * Replaces the 720px right drawer that clipped under the app bar and forced
 * horizontal scrolling. A fixture now opens as a full-screen audit dossier:
 *
 *  - dark-sage header band: fixture identity + every action in one place;
 *  - the CHAIN OF CUSTODY ribbon — the fixture's golden truth traced left to
 *    right (source project ▸ work order/specimen ▸ source document ▸ what's
 *    pinned), editable in place;
 *  - two-column body: the Expected-values table (finally wide enough to
 *    show every column) beside the latest result, re-baseline history,
 *    notes and payload.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  Paper,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import {
  Add,
  CameraAlt,
  ChevronRight,
  Close,
  DeleteOutline,
  ExpandLess,
  ExpandMore,
  PlayArrow,
  Save,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { forwardRef, useEffect, useState, type ReactElement, type Ref } from 'react';
import {
  deleteFixture,
  fetchFixture,
  replaceExpectations,
  runSingleFixture,
  updateFixture,
  updateSnapshot,
  type ExpectationDraft,
  type FixtureProvenance,
  type IntegrityCheck,
  type IntegrityFormulaLink,
  type IntegrityColumnChecksum,
  type IntegrityFixture,
  type IntegrityRunResult,
} from '../../../services/appIntegrityService';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { ChecksTable, ColumnChecksumsTable, statusChipColor } from './integrityDisplay';
import { ExpectationsEditor } from './ExpectationsEditor';
import { RebaselineDialog } from './RebaselineDialog';

const SlideUp = forwardRef(function SlideUp(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface FixtureDetailDialogProps {
  fixtureKey: string | null;
  latestResult?: IntegrityRunResult;
  onClose: () => void;
  onChanged: () => void;
  notify: (message: string, severity: 'success' | 'error' | 'info') => void;
}

/** One editable link in the chain-of-custody ribbon. */
const ChainField = ({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <Box sx={{ minWidth: 170, flex: '1 1 200px' }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontSize: '0.62rem',
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <TextField
      variant="standard"
      fullWidth
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        sx: {
          fontSize: '0.85rem',
          fontWeight: 600,
          '&:before': { borderBottomColor: 'transparent' },
          '&:hover:not(.Mui-disabled):before': { borderBottomColor: 'divider' },
        },
      }}
    />
  </Box>
);

const SectionCard = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
    <Box
      sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: '0.7rem',
          flex: 1,
        }}
      >
        {title}
      </Typography>
      {action}
    </Box>
    {children}
  </Paper>
);

export const FixtureDetailDialog = ({
  fixtureKey,
  latestResult,
  onClose,
  onChanged,
  notify,
}: FixtureDetailDialogProps) => {
  const theme = useTheme();
  const [fixture, setFixture] = useState<IntegrityFixture | null>(null);
  const [drafts, setDrafts] = useState<ExpectationDraft[]>([]);
  const [notes, setNotes] = useState('');
  const [provenance, setProvenance] = useState<FixtureProvenance>({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<'run' | 'snapshot' | 'save' | null>(null);
  const [adhocChecks, setAdhocChecks] = useState<IntegrityCheck[] | null>(null);
  const [adhocFormulas, setAdhocFormulas] = useState<Record<string, IntegrityFormulaLink> | undefined>(undefined);
  const [adhocChecksums, setAdhocChecksums] = useState<IntegrityColumnChecksum[] | undefined>(undefined);
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [rebaselineOpen, setRebaselineOpen] = useState(false);
  const [payloadOpen, setPayloadOpen] = useState(false);

  const loadDrafts = (data: IntegrityFixture) =>
    setDrafts(
      data.expectations.map((e) => ({
        resultPath: e.ResultPath,
        label: e.Label,
        expectedValue: e.ExpectedValue,
        toleranceAbs: e.ToleranceAbs,
        toleranceRel: e.ToleranceRel,
        unit: e.Unit,
        sortOrder: e.SortOrder,
      })),
    );

  useEffect(() => {
    setFixture(null);
    setAdhocChecks(null);
    setDirty(false);
    setPayloadOpen(false);
    if (!fixtureKey) return;
    void fetchFixture(fixtureKey).then((data) => {
      setFixture(data);
      setNotes(data.Notes ?? '');
      setProvenance(data.provenance ?? {});
      loadDrafts(data);
    });
  }, [fixtureKey]);

  const patchDraft = (index: number, patch: Partial<ExpectationDraft>) => {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
    setDirty(true);
  };

  const patchProvenance = (patch: Partial<FixtureProvenance>) => {
    setProvenance((p) => ({ ...p, ...patch }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!fixture) return;
    setBusy('save');
    try {
      await replaceExpectations(
        fixture.IntegrityFixtureKey,
        drafts.filter((d) => d.resultPath.trim()),
      );
      const provenanceChanged =
        JSON.stringify(provenance) !== JSON.stringify(fixture.provenance ?? {});
      if (notes !== (fixture.Notes ?? '') || provenanceChanged) {
        await updateFixture(fixture.IntegrityFixtureKey, {
          notes,
          ...(provenanceChanged ? { provenance } : {}),
        });
      }
      setDirty(false);
      notify('Fixture saved', 'success');
      onChanged();
    } catch {
      notify('Failed to save fixture', 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleRunOnce = async () => {
    if (!fixture) return;
    setBusy('run');
    setAdhocChecks(null);
    try {
      const { checks, formulas, columnChecksums } = await runSingleFixture(fixture.IntegrityFixtureKey);
      setAdhocChecks(checks);
      setAdhocFormulas(formulas);
      setAdhocChecksums(columnChecksums);
      const failed = checks.filter((c) => !c.pass).length;
      notify(
        failed ? `Run finished — ${failed} of ${checks.length} checks failed` : 'Run finished — all checks passed',
        failed ? 'error' : 'success',
      );
    } catch (error) {
      notify(
        (error as { response?: { data?: { message?: string } } }).response?.data?.message ??
          'Fixture run failed',
        'error',
      );
    } finally {
      setBusy(null);
    }
  };

  const handleRebaseline = async (justification: string) => {
    if (!fixture) return;
    setBusy('snapshot');
    try {
      const { updated, checks } = await updateSnapshot(fixture.IntegrityFixtureKey, justification);
      setAdhocChecks(checks);
      setRebaselineOpen(false);
      notify(`Golden re-baselined — ${updated} expected value(s) replaced`, 'success');
      const fresh = await fetchFixture(fixture.IntegrityFixtureKey);
      setFixture(fresh);
      loadDrafts(fresh);
      onChanged();
    } catch (error) {
      notify(
        (error as { response?: { data?: { error?: string } } }).response?.data?.error ??
          'Golden re-baseline failed',
        'error',
      );
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = () => {
    if (!fixture) return;
    setConfirm({
      open: true,
      title: 'Delete fixture?',
      message: `"${fixture.FixtureName}" will be removed from the suite (soft delete).`,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      onConfirm: async () => {
        setConfirm(CONFIRM_INITIAL);
        try {
          await deleteFixture(fixture.IntegrityFixtureKey);
          notify('Fixture deleted', 'success');
          onClose();
          onChanged();
        } catch {
          notify('Delete failed', 'error');
        }
      },
    });
  };

  const checks = adhocChecks ?? latestResult?.checks ?? [];
  const formulas = adhocChecks ? adhocFormulas : latestResult?.formulas;
  const columnChecksums = adhocChecks ? adhocChecksums : latestResult?.columnChecksums;
  const status = latestResult?.Status;

  return (
    <Dialog fullScreen open={!!fixtureKey} onClose={onClose} TransitionComponent={SlideUp}>
      {!fixture ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* ── Header band ─────────────────────────────────────────────── */}
          <Box
            sx={{
              backgroundColor: theme.customColors.appBar,
              color: theme.palette.common.white,
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: '0.18em', opacity: 0.7, display: 'block', lineHeight: 1.4 }}
              >
                Integrity fixture
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                {fixture.FixtureName}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={fixture.TestName}
                  sx={{ color: 'inherit', borderColor: alpha(theme.palette.common.white, 0.4) }}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={fixture.AdapterId}
                  sx={{ color: 'inherit', borderColor: alpha(theme.palette.common.white, 0.4), fontFamily: 'monospace' }}
                  variant="outlined"
                />
                {status && (
                  <Chip size="small" color={statusChipColor(status)} label={`last run: ${status.toUpperCase()}`} />
                )}
                {!fixture.IsActive && (
                  <Chip
                    size="small"
                    label="INACTIVE — skipped by suite runs"
                    sx={{ backgroundColor: alpha(theme.palette.common.white, 0.15), color: 'inherit' }}
                  />
                )}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                color="primary"
                startIcon={busy === 'run' ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />}
                disabled={busy !== null}
                onClick={handleRunOnce}
              >
                Run this fixture
              </Button>
              <Tooltip title="Re-run and replace every golden expectation with current outputs — requires a written justification; the superseded baseline is kept in the golden history">
                <span>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={busy === 'snapshot' ? <CircularProgress size={14} color="inherit" /> : <CameraAlt />}
                    disabled={busy !== null}
                    onClick={() => setRebaselineOpen(true)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Re-baseline golden…
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Delete this fixture (soft delete)">
                <IconButton
                  onClick={handleDelete}
                  disabled={busy !== null}
                  sx={{ color: alpha(theme.palette.common.white, 0.7) }}
                >
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} sx={{ color: 'inherit' }} aria-label="Close">
                <Close />
              </IconButton>
            </Stack>
          </Box>

          {/* ── Chain of custody: where the golden truth comes from ─────── */}
          <Box
            sx={{
              backgroundColor: theme.palette.background.default,
              borderBottom: 1,
              borderColor: 'divider',
              px: 3,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <ChainField
              label="Source project"
              placeholder="e.g. 2609007 Penasquito"
              value={provenance.sourceProject ?? ''}
              onChange={(v) => patchProvenance({ sourceProject: v || null })}
            />
            <ChevronRight fontSize="small" sx={{ color: 'text.disabled' }} />
            <ChainField
              label="Work order / specimen"
              placeholder="e.g. POC-076-25 @ 61.88 m"
              value={provenance.sourceWorkOrder ?? ''}
              onChange={(v) => patchProvenance({ sourceWorkOrder: v || null })}
            />
            <ChevronRight fontSize="small" sx={{ color: 'text.disabled' }} />
            <ChainField
              label="Source document"
              placeholder="e.g. DS Workbook.xlsm (Sheet 001)"
              value={provenance.sourceDocument ?? ''}
              onChange={(v) => patchProvenance({ sourceDocument: v || null })}
            />
            <ChevronRight fontSize="small" sx={{ color: 'text.disabled' }} />
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontSize: '0.62rem',
                  color: 'text.secondary',
                }}
              >
                Pinned with fixture
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
                {fixture.files.length === 0 && (
                  <Typography variant="caption" color="text.disabled">none</Typography>
                )}
                {fixture.files.map((file) => (
                  <Chip
                    key={file.IntegrityFixtureFileKey}
                    size="small"
                    variant="outlined"
                    label={file.FileName}
                    sx={{ fontFamily: 'monospace', fontSize: '0.66rem', height: 20 }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>

          {/* ── Body: app-style fixed panes — each COLUMN scrolls on its
                 own, the page itself never scrolls (Vahid 2026-07-31:
                 "more space, but you still have to scroll up and down"). ── */}
          <Box sx={{ flex: 1, overflow: { xs: 'auto', lg: 'hidden' }, p: 3, backgroundColor: theme.palette.background.default }}>
            <Box
              sx={{
                display: 'grid',
                gap: 2.5,
                alignItems: 'start',
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
                maxWidth: 1700,
                mx: 'auto',
                height: { lg: '100%' },
              }}
            >
              {/* LEFT — the golden expectations */}
              <Stack spacing={2.5} sx={{ minWidth: 0, height: { lg: '100%' }, overflowY: { lg: 'auto' }, pr: { lg: 0.5 } }}>
                <SectionCard
                  title={`Expected values (${drafts.length})`}
                  action={
                    <>
                      <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() => {
                          setDrafts((prev) => [
                            ...prev,
                            { resultPath: '', label: '', expectedValue: '', toleranceAbs: null, toleranceRel: null, unit: null, sortOrder: prev.length },
                          ]);
                          setDirty(true);
                        }}
                      >
                        Add check
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={busy === 'save' ? <CircularProgress size={14} color="inherit" /> : <Save />}
                        disabled={!dirty || busy !== null}
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                    </>
                  }
                >
                  <Box sx={{ maxHeight: '62vh', overflow: 'auto' }}>
                    <ExpectationsEditor
                      drafts={drafts}
                      onPatch={patchDraft}
                      onRemove={(index) => {
                        setDrafts((prev) => prev.filter((_, i) => i !== index));
                        setDirty(true);
                      }}
                    />
                  </Box>
                </SectionCard>

                <SectionCard title="Notes">
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Where these values were verified, quirks of the source data…"
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setDirty(true);
                    }}
                    sx={{ '& fieldset': { border: 'none' } }}
                    InputProps={{ sx: { fontSize: '0.85rem' } }}
                  />
                </SectionCard>

                <SectionCard
                  title="Input payload (what gets replayed)"
                  action={
                    <IconButton size="small" onClick={() => setPayloadOpen((o) => !o)}>
                      {payloadOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  }
                >
                  <Collapse in={payloadOpen}>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        px: 2,
                        py: 1.5,
                        maxHeight: 420,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.72rem',
                        backgroundColor: theme.table.stripeBg,
                      }}
                    >
                      {formatPayload(fixture.InputPayload)}
                    </Box>
                  </Collapse>
                  {!payloadOpen && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
                      {fixture.InputPayload.length.toLocaleString()} characters — expand to inspect the exact
                      request bodies the replay sends.
                    </Typography>
                  )}
                </SectionCard>
              </Stack>

              {/* RIGHT — what the app said, and how the golden has moved */}
              <Stack spacing={2.5} sx={{ minWidth: 0, height: { lg: '100%' }, overflowY: { lg: 'auto' }, pr: { lg: 0.5 } }}>
                <SectionCard title={adhocChecks ? 'Latest ad-hoc run' : 'Latest suite result'}>
                  {/* Wide tables scroll inside the card — never clip. */}
                  <Box sx={{ p: 2, overflowX: 'auto' }}>
                    {checks.length ? (
                      <>
                        <ChecksTable checks={checks} formulas={formulas} />
                        <ColumnChecksumsTable checksums={columnChecksums} />
                      </>
                    ) : (
                      <Alert severity="info" sx={{ py: 0 }}>
                        No results yet — run the fixture to see expected vs actual.
                      </Alert>
                    )}
                  </Box>
                </SectionCard>

                {(fixture.goldenHistory?.length ?? 0) > 0 && (
                  <SectionCard title={`Golden re-baseline history (${fixture.goldenHistory!.length})`}>
                    <Stack spacing={0} sx={{ p: 2, pt: 1.5 }}>
                      {fixture.goldenHistory!.slice().reverse().map((h, i) => (
                        <Box
                          key={i}
                          sx={{
                            position: 'relative',
                            pl: 2.5,
                            pb: 1.5,
                            borderLeft: 2,
                            borderColor: alpha(theme.palette.primary.main, 0.25),
                            '&:last-child': { pb: 0.5 },
                            '&:before': {
                              content: '""',
                              position: 'absolute',
                              left: -5,
                              top: 4,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.primary.main,
                            },
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                            {new Date(h.at).toLocaleString()} — {h.by}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {h.justification}
                          </Typography>
                          {h.changedExpectations.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {h.changedExpectations.length} value(s) changed — e.g.{' '}
                              {h.changedExpectations[0].label}: {h.changedExpectations[0].from} →{' '}
                              {h.changedExpectations[0].to}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </SectionCard>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      )}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(CONFIRM_INITIAL)} />
      {fixture && (
        <RebaselineDialog
          open={rebaselineOpen}
          fixtureName={fixture.FixtureName}
          busy={busy === 'snapshot'}
          onClose={() => setRebaselineOpen(false)}
          onConfirm={handleRebaseline}
        />
      )}
    </Dialog>
  );
};

function formatPayload(payload: string): string {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}
